import React from 'react';
import styled from 'styled-components';
import { Typography, Colors, AnalyticsProps } from '@inplayer-org/inplayer-ui';
import { transparentize } from 'polished';

// images
import defaultPreviewImg from 'assets/pics/ip-preview-premium.jpg';

// utils
import truncateTextWithTooltip from 'utils/truncateTextWithTooltip';
import { formatDate } from 'utils/formatDate';

interface Props {
  id: number;
  title: string;
  dateCreated?: number;
  dateUpdated?: number;
  contentType: string;
  coverImage?: string;
  renderButton?: () => void;
}

const Container = styled.div<AnalyticsProps>`
  background-color: ${Colors.white};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.font.sizes.small};
  width: 100%;
  padding: 1.2rem;
  box-shadow: 0 0 17px 2px ${transparentize(0.13, Colors.darkGray)};
`;

const GridImage = styled.img`
  width: 100%;
  border-radius: 10px;
  margin-bottom: 1rem;
  min-height: 20%;
`;

interface RowProps {
  justifyContent?: boolean;
  alignItems?: boolean;
}
const Row = styled.div<RowProps>`
  display: flex;
  flex-direction: column;
  margin: 0.3rem 0;
  align-items: ${({ alignItems }) => alignItems && 'flex-end'};
  justify-content: ${({ justifyContent }) => justifyContent && 'flex-end'};
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled(Typography)`
  color: ${Colors.fontDarkGray};
`;

interface ContentProps {
  smallFontSize?: boolean;
}
const Content = styled(Typography)<ContentProps>`
  color: ${Colors.fontGray};
  font-size: ${({ smallFontSize, theme }) => smallFontSize && theme.font.sizes.extraSmall};
`;

const CardGrid: React.FC<Props> = ({
  id,
  title,
  contentType,
  dateCreated,
  dateUpdated,
  coverImage = defaultPreviewImg,
  renderButton,
}) => {
  return (
    <Container tag={`box_${id}`}>
      <GridImage alt="thumbnail-preview-img" src={coverImage} />
      <InfoContainer>
        <Row>
          <Title variant="p">{truncateTextWithTooltip({ text: title, textLimit: 20 })}</Title>
          <Content variant="p">{`${contentType.toUpperCase()} Content`}</Content>
        </Row>
        <Row alignItems>
          <Content smallFontSize variant="p">
            ID: {id}
          </Content>
          {dateCreated && (
            <Content smallFontSize variant="p">{`Created: ${formatDate(dateCreated, 'YYYY/DD/MM')}`}</Content>
          )}
          {dateUpdated && (
            <Content smallFontSize variant="p">{`Updated: ${formatDate(dateUpdated, 'YYYY/DD/MM')}`}</Content>
          )}
          {renderButton && renderButton()}
        </Row>
      </InfoContainer>
    </Container>
  );
};

export default CardGrid;
